import React, { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { safeStorage } from '../lib/safeStorage';

/**
 * PrivateBrowsingBanner
 * Shows a non-blocking warning when the browser is in Private/Incognito mode
 * (or has localStorage disabled), so users understand their session won't
 * survive a browser restart and they may need to sign in again.
 *
 * Detection: safeStorage.isPersistent() returns false when the localStorage
 * probe failed (e.g. iOS Safari Private Browsing throws QuotaExceededError).
 *
 * Dismissal is in-memory only (we can't persist anything in private mode by
 * definition), so the banner reappears on full reload — which is the correct
 * behaviour: the user needs the reminder again next session.
 */
function PrivateBrowsingBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Defer the probe so it runs after first paint
    const id = setTimeout(() => {
      if (!safeStorage.isPersistent()) {
        setShow(true);
      }
    }, 300);
    return () => clearTimeout(id);
  }, []);

  if (!show) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-amber-50 border-b-2 border-amber-400 px-4 py-2.5 flex items-start sm:items-center gap-3"
      data-testid="private-browsing-banner"
      role="status"
    >
      <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5 sm:mt-0" />
      <p className="text-xs sm:text-sm text-amber-900 leading-snug flex-1">
        <span className="font-semibold uppercase tracking-wider">Private browsing detected.</span>{' '}
        Your sign-in won't persist after you close this tab. For the full Axiom experience, please use a regular browser window.
      </p>
      <button
        type="button"
        onClick={() => setShow(false)}
        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="dismiss-private-browsing-banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default PrivateBrowsingBanner;
